import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery,
  alpha,
  Fade,
  Slide,
  Zoom,
  Dialog,
  DialogContent,
  IconButton
} from '@mui/material';
import {
  ArrowRight,
  X,
  Calendar,
  MapPin,
  Users,
  Hotel,
  Star,
  Award,
  Lightbulb,
  CheckCircle,
  Eye,
  Sparkles,
  Utensils,
  Coffee,
  Wine,
  Globe
} from 'lucide-react';

// Animated background shape component
const AnimatedShape = ({ top, left, size, delay, color, type }) => {
  const theme = useTheme();
  
  return (
    <Box
      sx={{
        position: 'absolute',
        top: top,
        left: left,
        width: size,
        height: size,
        opacity: 0.1,
        background: color === 'primary' ? theme.palette.primary.main : theme.palette.primary.light,
        borderRadius: type === 'circle' ? '50%' : '30% 70% 70% 30% / 30% 30% 70% 70%',
        animation: type === 'circle' 
          ? `float 10s ease-in-out ${delay}s infinite` 
          : `morphBlob 20s ease-in-out ${delay}s infinite`,
        zIndex: 0,
        filter: 'blur(60px)',
        '@keyframes float': {
          '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
          '50%': { transform: 'translateY(-20px) rotate(5deg)' }
        },
        '@keyframes morphBlob': {
          '0%, 100%': { borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%' },
          '25%': { borderRadius: '70% 30% 30% 70% / 70% 70% 30% 30%' },
          '50%': { borderRadius: '30% 70% 30% 70% / 70% 30% 70% 30%' },
          '75%': { borderRadius: '70% 30% 70% 30% / 30% 70% 30% 70%' }
        }
      }}
    />
  );
};

// Project Card Component
const ProjectCard = ({ project, delay, onClick }) => {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);

  return (
    <Fade in={true} style={{ transitionDelay: `${delay}ms` }}>
      <Card 
        sx={{ 
          height: '100%',
          borderRadius: 4,
          overflow: 'hidden',
          position: 'relative',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          background: theme.customColors.backgrounds.glass,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.1)',
          '&:hover': {
            transform: 'translateY(-10px)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
          },
          '&:hover .project-overlay': { opacity: 1 },
          '&:hover .project-image': { transform: 'scale(1.1)' }
        }}
        onClick={() => onClick(project)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Box 
          sx={{ 
            position: 'relative', 
            height: 280,
            overflow: 'hidden',
            background: project.gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box
            className="project-image"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 3,
              transition: 'transform 0.6s ease',
              p: 4,
              width: '100%',
              height: '100%',
              alignItems: 'center',
              justifyItems: 'center'
            }}
          >
            {project.icons.map((IconComponent, index) => (
              <Box
                key={index}
                sx={{
                  color: 'white',
                  opacity: 0.9,
                  animation: `iconBounce 2s ease-in-out ${index * 0.3}s infinite alternate`,
                  transform: hovered ? `scale(${1.2 + (index % 2) * 0.2})` : `scale(${1 + (index % 2) * 0.1})`,
                  transition: 'transform 0.3s ease',
                  '@keyframes iconBounce': {
                    '0%': { transform: 'translateY(0)' },
                    '100%': { transform: 'translateY(-8px)' }
                  }
                }}
              >
                <IconComponent size={36 + index * 2} />
              </Box>
            ))}
          </Box>
          
          <Box
            className="project-overlay"
            sx={{
              position: 'absolute',
              inset: 0,
              background: theme.customColors.gradients.overlay,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              p: 3,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Hotel size={16} style={{ marginRight: 8, color: theme.palette.primary.main }} />
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 600 }}>
                {project.type}
              </Typography>
            </Box>
            <Typography variant="h6" color="text.primary" gutterBottom sx={{ fontWeight: 600 }}>
              {project.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {project.description}
            </Typography>
            <Button 
              variant="outlined"
              size="small"
              endIcon={<Eye size={16} />}
              sx={{ 
                alignSelf: 'flex-start',
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': { borderColor: 'white', background: 'rgba(255,255,255,0.1)' }
              }}
            >
              View Details
            </Button>
          </Box>
        </Box>
      </Card>
    </Fade>
  );
};

// Project Detail Modal
const ProjectDetailModal = ({ project, open, onClose }) => {
  const theme = useTheme();
  
  if (!project) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(to bottom, #212121, #000000)',
          borderRadius: 4,
          border: '1px solid rgba(255,255,255,0.1)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
              background: 'rgba(0,0,0,0.8)',
              color: 'white',
              '&:hover': { background: 'rgba(0,0,0,0.9)' }
            }}
          >
            <X size={24} />
          </IconButton>

          <Box
            sx={{
              height: 300,
              background: project.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 3,
                p: 4,
                width: '100%',
                height: '100%',
                alignItems: 'center',
                justifyItems: 'center'
              }}
            >
              {[...project.icons, ...project.icons.slice(0, 2)].map((IconComponent, index) => (
                <Box
                  key={index}
                  sx={{
                    color: 'white',
                    opacity: 0.8,
                    animation: `modalIconFloat 3s ease-in-out ${index * 0.2}s infinite alternate`,
                    '@keyframes modalIconFloat': {
                      '0%': { transform: 'translateY(0) rotate(0deg)' },
                      '100%': { transform: 'translateY(-10px) rotate(5deg)' }
                    }
                  }}
                >
                  <IconComponent size={28 + (index % 3) * 6} />
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Hotel size={20} style={{ marginRight: 12, color: theme.palette.primary.main }} />
              <Typography variant="overline" color="primary.main" sx={{ fontWeight: 600 }}>
                {project.type}
              </Typography>
            </Box>

            <Typography variant="h4" color="text.primary" gutterBottom sx={{ fontWeight: 700 }}>
              {project.title}
            </Typography>

            <Typography variant="body1" color="text.secondary" paragraph>
              {project.fullDescription}
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Calendar size={18} style={{ marginRight: 12, color: theme.palette.primary.main }} />
                  <Box>
                    <Typography variant="body2" color="text.disabled">Completion Date</Typography>
                    <Typography variant="body1" color="text.primary">{project.completionDate}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <MapPin size={18} style={{ marginRight: 12, color: theme.palette.primary.main }} />
                  <Box>
                    <Typography variant="body2" color="text.disabled">Location</Typography>
                    <Typography variant="body1" color="text.primary">{project.location}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Users size={18} style={{ marginRight: 12, color: theme.palette.primary.main }} />
                  <Box>
                    <Typography variant="body2" color="text.disabled">Area</Typography>
                    <Typography variant="body1" color="text.primary">{project.area}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Users size={18} style={{ marginRight: 12, color: theme.palette.primary.main }} />
                  <Box>
                    <Typography variant="body2" color="text.disabled">Capacity</Typography>
                    <Typography variant="body1" color="text.primary">{project.capacity}</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {project.features && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" color="text.primary" gutterBottom>Key Features</Typography>
                <Grid container spacing={1}>
                  {project.features.map((feature, index) => (
                    <Grid item key={index}>
                      <Chip
                        label={feature}
                        size="small"
                        sx={{
                          background: alpha(theme.palette.primary.main, 0.2),
                          color: theme.palette.primary.light,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const HospitalityPortfolio = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showElements, setShowElements] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowElements(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const hospitalityProjects = [
    {
      id: 1,
      title: 'Boutique Hotel Lobby',
      type: 'Hotel',
      description: 'Luxurious lobby design that creates lasting first impressions',
      fullDescription: 'A sophisticated hotel lobby that combines elegance with functionality. The design features premium materials, comfortable seating areas, and a reception desk that facilitates smooth check-in processes while creating an atmosphere of luxury.',
      icons: [Hotel, Star, Globe, Award, Sparkles, CheckCircle],
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      location: 'Gachibowli, Hyderabad',
      completionDate: 'December 2023',
      area: '3,500 sq ft',
      capacity: '150 guests',
      features: ['Reception Desk', 'Lounge Area', 'Concierge Service', 'Art Installations', 'Premium Lighting', 'VIP Seating']
    },
    {
      id: 2,
      title: 'Fine Dining Restaurant',
      type: 'Restaurant',
      description: 'Elegant dining space that enhances culinary experiences',
      fullDescription: 'An upscale restaurant design that creates the perfect ambiance for fine dining. Features include intimate booth seating, an open kitchen concept, wine display areas, and carefully curated lighting that enhances the dining experience.',
      icons: [Utensils, Star, Lightbulb, Wine, Award, CheckCircle],
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      location: 'Jubilee Hills, Hyderabad',
      completionDate: 'November 2023',
      area: '4,200 sq ft',
      capacity: '120 diners',
      features: ['Open Kitchen', 'Wine Cellar', 'Private Dining', 'Bar Area', 'Outdoor Seating', 'Chef\'s Table']
    },
    {
      id: 3,
      title: 'Resort Spa & Wellness',
      type: 'Spa',
      description: 'Tranquil wellness space for relaxation and rejuvenation',
      fullDescription: 'A serene spa environment designed to promote relaxation and wellness. The space includes treatment rooms, meditation areas, a reception lounge, and wellness facilities that create a peaceful retreat from daily stress.',
      icons: [Sparkles, Lightbulb, Star, CheckCircle, Award, Globe],
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      location: 'Outer Ring Road, Hyderabad',
      completionDate: 'October 2023',
      area: '6,000 sq ft',
      capacity: '40 treatments/day',
      features: ['Treatment Rooms', 'Relaxation Lounge', 'Steam Room', 'Meditation Space', 'Changing Rooms', 'Retail Area']
    },
    {
      id: 4,
      title: 'Coffee Shop & Café',
      type: 'Café',
      description: 'Cozy café atmosphere perfect for work and socializing',
      fullDescription: 'A modern café design that balances comfort with functionality. Features include various seating options for different needs, a display counter for pastries, an efficient service area, and ambient lighting that creates a welcoming atmosphere.',
      icons: [Coffee, Globe, Lightbulb, Users, Star, CheckCircle],
      gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
      location: 'Banjara Hills, Hyderabad',
      completionDate: 'September 2023',
      area: '2,800 sq ft',
      capacity: '80 guests',
      features: ['Counter Service', 'Lounge Seating', 'Work Areas', 'Outdoor Patio', 'Display Counter', 'Wi-Fi Zones']
    },
    {
      id: 5,
      title: 'Event Banquet Hall',
      type: 'Banquet',
      description: 'Versatile event space for memorable celebrations',
      fullDescription: 'A flexible banquet hall designed to accommodate various types of events. The space features movable partitions, multiple lighting scenarios, a built-in stage area, and service facilities that ensure seamless event execution.',
      icons: [Star, Users, Lightbulb, Award, Globe, CheckCircle],
      gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      location: 'HITEC City, Hyderabad',
      completionDate: 'August 2023',
      area: '8,500 sq ft',
      capacity: '300 guests',
      features: ['Flexible Layout', 'Stage Area', 'Audio/Visual', 'Catering Kitchen', 'VIP Lounge', 'Parking Facilities']
    },
    {
      id: 6,
      title: 'Hotel Suite Design',
      type: 'Suite',
      description: 'Luxury accommodation that feels like home',
      fullDescription: 'Premium hotel suite design that provides guests with a home-away-from-home experience. Features include a spacious living area, bedroom with luxury bedding, kitchenette, work space, and bathroom with premium fixtures.',
      icons: [Hotel, Star, Lightbulb, Globe, Award, Sparkles],
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      location: 'Madhapur, Hyderabad',
      completionDate: 'July 2023',
      area: '1,200 sq ft',
      capacity: '4 guests',
      features: ['Living Area', 'Bedroom Suite', 'Kitchenette', 'Work Desk', 'Luxury Bathroom', 'Balcony View']
    }
  ];

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setModalOpen(true);
  };

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box 
        sx={{ 
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          background: theme.palette.background.default,
          pt: { xs: 15, md: 20 },
          pb: { xs: 10, md: 15 },
          overflow: 'hidden'
        }}
      >
        <AnimatedShape top="10%" left="5%" size="300px" delay={0} color="primary" type="blob" />
        <AnimatedShape top="60%" left="80%" size="200px" delay={0.5} color="primary" type="circle" />
        <AnimatedShape top="30%" left="60%" size="350px" delay={1} color="secondary" type="blob" />
        
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={8}>
              <Fade in={showElements} timeout={1000}>
                <Box>
                  <Box 
                    sx={{ 
                      display: 'inline-block', 
                      px: 2, 
                      py: 0.5, 
                      bgcolor: theme.customColors.backgrounds.glass,
                      backdropFilter: 'blur(4px)',
                      borderRadius: 5,
                      mb: 3,
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}
                  >
                    <Typography variant="subtitle2" color="primary.main">
                      HOSPITALITY PORTFOLIO
                    </Typography>
                  </Box>
                  
                  <Typography 
                    variant="h1" 
                    component="h1" 
                    color="text.primary"
                    sx={{ 
                      fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                      mb: 3 
                    }}
                  >
                    Creating{' '}
                    <Box component="span" sx={{ position: 'relative' }}>
                      <Box component="span" color="primary.main">Experiences</Box>
                      <Box
                        component="span"
                        sx={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          bottom: { xs: -4, md: -8 },
                          height: 4,
                          backgroundColor: 'primary.main',
                          borderRadius: 2
                        }}
                      />
                    </Box>
                    <Box component="span" display="block" mt={1}>
                      That Last{' '}
                      <Box component="span" color="primary.main">Forever</Box>
                    </Box>
                  </Typography>
                  
                  <Typography 
                    variant="h6" 
                    component="p" 
                    color="text.secondary"
                    sx={{ maxWidth: 600, mb: 5 }}
                  >
                    We design hospitality spaces that create memorable experiences for guests. 
                    From boutique hotels to fine dining restaurants, every detail is crafted to exceed expectations.
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ 
                        width: 48, 
                        height: 48, 
                        background: theme.customColors.gradients.primary,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2
                      }}>
                        <Hotel size={24} color="white" />
                      </Box>
                      <Typography color="text.primary">
                        {hospitalityProjects.length} Hospitality Projects
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ 
                        width: 48, 
                        height: 48, 
                        background: theme.customColors.gradients.primary,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2
                      }}>
                        <Star size={24} color="white" />
                      </Box>
                      <Typography color="text.primary">
                        Guest-Focused Design
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Slide direction="left" in={showElements} timeout={1000}>
                <Box
                  sx={{
                    position: 'relative',
                    height: 400,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '80%',
                      height: '80%',
                      borderRadius: 4,
                      background: theme.customColors.gradients.primary,
                      opacity: 0.3,
                      filter: 'blur(20px)',
                      animation: 'pulse 5s infinite alternate',
                    }}
                  />
                  
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: 3,
                      width: '100%',
                      maxWidth: 240,
                      zIndex: 2
                    }}
                  >
                    {[Hotel, Utensils, Star, Sparkles, Coffee, Globe, Award, Lightbulb, CheckCircle].map((IconComponent, index) => (
                      <Zoom key={index} in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            borderRadius: '12px',
                            background: theme.customColors.gradients.primary,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            boxShadow: theme.customColors.shadows.primary,
                            animation: `iconFloat 3s ease-in-out ${index * 0.1}s infinite alternate`,
                            '@keyframes iconFloat': {
                              '0%': { transform: 'translateY(0)' },
                              '100%': { transform: 'translateY(-8px)' }
                            }
                          }}
                        >
                          <IconComponent size={24} />
                        </Box>
                      </Zoom>
                    ))}
                  </Box>
                </Box>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Projects Grid */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          position: 'relative',
          overflow: 'hidden',
          background: theme.customColors.gradients.background1
        }}
      >
        <AnimatedShape top="20%" right="5%" size="250px" delay={0.2} color="primary" type="blob" />
        <AnimatedShape top="70%" left="10%" size="200px" delay={0.8} color="secondary" type="circle" />
        
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Fade in={showElements}>
              <Box>
                <Typography variant="h2" component="h2" color="text.primary" sx={{ mb: 2 }}>
                  Hospitality Projects
                </Typography>
                <Typography variant="h6" component="p" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
                  Spaces designed to create unforgettable guest experiences.
                </Typography>
              </Box>
            </Fade>
          </Box>
          
          <Grid container spacing={4}>
            {hospitalityProjects.map((project, index) => (
              <Grid item xs={12} sm={6} lg={4} key={project.id}>
                <ProjectCard 
                  project={project} 
                  delay={index * 200}
                  onClick={handleProjectClick}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.9)}, ${alpha(theme.palette.secondary.main, 0.95)})`
        }}
      >
        <Container maxWidth="md">
          <Fade in={showElements}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h2" component="h2" color="text.primary" sx={{ mb: 3 }}>
                Ready to Create Memorable Experiences?
              </Typography>
              <Typography variant="h5" component="p" color="text.primary" sx={{ mb: 6, opacity: 0.9 }}>
                Let's design a hospitality space that delights guests and drives success.
              </Typography>
              <Button
                href="/contact"
                variant="contained"
                size="large"
                endIcon={<ArrowRight />}
                sx={{
                  background: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: 'white',
                  '&:hover': { background: 'rgba(255,255,255,0.3)' }
                }}
              >
                Start Your Hospitality Project
              </Button>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Project Detail Modal */}
      <ProjectDetailModal 
        project={selectedProject}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </Box>
  );
};

export default HospitalityPortfolio;